import { formatApiErrorDetail } from "@/lib/api";
/**
 * Walk Mode — full-screen one-item-at-a-time stepper for ProjectRadar
 * walkthroughs. Field worker taps through building 1..N, snaps photos,
 * fills notes + ballpark $, next. At the end: run AI Estimate → mint
 * Tradesman Link for a sub quote → download the Amended Quote PDF for
 * landlord approval.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowLeft, ArrowRight, Camera, ImageSquare, Sparkle, Wrench, FilePdf, CheckCircle } from "@phosphor-icons/react";
import AIAssessModal from "./AIAssessModal";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token")}` } });

export default function WalkMode({ project, onClose, onChanged }) {
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const items = useMemo(() => project?.punch_items || [], [project]);
  const total = items.length;
  const item = items[idx];
  const isLast = idx === total - 1;

  if (!item) return null;

  const patch = async (payload) => {
    setBusy(true);
    try {
      await axios.patch(`${BASE}/api/projects/${project.id || project._id}/punch/${item.id}`, payload, auth());
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't save"));
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    if (!e.target.files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(e.target.files)) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${BASE}/api/projects/${project.id || project._id}/punch/${item.id}/photo`,
          fd, { ...auth(), headers: { ...auth().headers, "Content-Type": "multipart/form-data" } });
      }
      toast.success("Photo added");
      onChanged?.();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const complete = async () => {
    await patch({ status: "complete" });
    if (!isLast) setIdx(i => i + 1);
  };

  const sendToSub = async () => {
    try {
      const r = await axios.post(`${BASE}/api/projects/${project.id || project._id}/tradesman-links`,
        { label: "Garage sub · quote request", trade: "general_sub", use_pin: false }, auth());
      const url = `${window.location.origin}/tradesman/${r.data.token}`;
      const body = `Hey — need a quote on ${total} garage doors at ${project.property_address}. Full scope + photos: ${url} — reply with $ per door.`;
      window.location.href = `sms:?body=${encodeURIComponent(body)}`;
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't mint sub link")); }
  };

  const downloadPDF = () => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    window.open(`${BASE}/api/projects/${project.id || project._id}/amended-quote.pdf?auth=${encodeURIComponent(token)}&owner_name=Leslie&prepared_by=Andrew+Harman+/+PMR`, "_blank");
  };

  const photos = item.photos || [];
  const needMore = Math.max(0, (item.min_photos || 0) - photos.length);
  const pct = Math.round(((idx + 1) / total) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 overflow-auto" data-testid="walk-mode">
      <header className="sticky top-0 z-10 bg-[#0f172a] text-white p-3 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">Walk Mode · {idx + 1} of {total}</div>
            <div className="font-display font-black text-base leading-tight truncate">{project.name}</div>
          </div>
          <button onClick={onClose} data-testid="walk-close" className="h-9 w-9 flex items-center justify-center border border-slate-600"><X size={18}/></button>
        </div>
        <div className="h-1 bg-slate-700"><div className="h-full bg-[#00C4CC]" style={{ width: `${pct}%` }}/></div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="p-3 bg-white border-2 border-[#0f172a]">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">📍 {item.room}</div>
          <div className="font-display font-black text-2xl uppercase tracking-tight">{item.title}</div>
          {item.status === "complete" && <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 inline-flex items-center gap-1"><CheckCircle weight="fill" size={12}/> Complete</div>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="h-14 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 cursor-pointer">
            <Camera weight="fill" size={20}/> Photo
            <input type="file" accept="image/*" capture="environment" onChange={uploadPhoto} className="hidden"/>
          </label>
          <label className="h-14 bg-slate-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 cursor-pointer">
            <ImageSquare weight="fill" size={20}/> Gallery
            <input type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden"/>
          </label>
        </div>
        {photos.length > 0 && (
          <div className="p-2 bg-white border-2 border-slate-300 flex gap-1 overflow-x-auto">
            {photos.map((p, i) => <img key={i} src={`${BASE}/api/files/${p}`} alt="" className="w-20 h-20 object-cover border border-[#0f172a]"/>)}
          </div>
        )}
        {needMore > 0 && (
          <div className="p-2 bg-amber-100 border-2 border-amber-500 text-[10px] font-black uppercase tracking-widest text-amber-900 text-center">
            📸 Need {needMore} more photo{needMore === 1 ? "" : "s"}
          </div>
        )}

        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Notes / Findings</div>
          <textarea rows={12} defaultValue={item.description || ""}
            onBlur={e => e.target.value !== (item.description || "") && patch({ description: e.target.value })}
            data-testid="walk-notes"
            className="w-full border-2 border-[#0f172a] p-2 text-xs font-mono"/>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Ballpark $</div>
            <input type="number" defaultValue={item.cost_cents ? (item.cost_cents/100).toFixed(0) : ""}
              onBlur={e => patch({ cost_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
              data-testid="walk-cost"
              className="w-full h-11 border-2 border-[#0f172a] px-2 text-sm"/>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Priority</div>
            <select defaultValue={item.priority || "med"} onChange={e => patch({ priority: e.target.value })}
              data-testid="walk-priority"
              className="w-full h-11 border-2 border-[#0f172a] px-2 text-sm bg-white">
              <option value="low">low</option><option value="med">med</option>
              <option value="high">high</option><option value="urgent">urgent</option>
            </select>
          </div>
        </div>

        <button onClick={complete} disabled={busy}
          data-testid="walk-complete"
          className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40">
          <CheckCircle weight="fill" size={20}/> Mark Complete {isLast ? "· Finish Walk" : "· Next Building"}
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
            data-testid="walk-prev"
            className="h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest bg-white hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-1">
            <ArrowLeft weight="bold" size={14}/> Prev
          </button>
          <button onClick={() => setIdx(i => Math.min(total - 1, i + 1))} disabled={isLast}
            data-testid="walk-next"
            className="h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest bg-white hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-1">
            Next <ArrowRight weight="bold" size={14}/>
          </button>
        </div>

        {isLast && (
          <div className="mt-6 space-y-2 p-4 bg-white border-4 border-emerald-500" data-testid="walk-final-actions">
            <div className="font-display font-black text-lg uppercase tracking-tight text-center mb-3">🎯 You Made It. Now What?</div>
            <button onClick={() => setShowAI(true)}
              data-testid="walk-ai"
              className="w-full h-12 bg-[#00C4CC] hover:bg-cyan-600 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2">
              <Sparkle weight="fill" size={18}/> Run AI Estimate + Write-Up
            </button>
            <button onClick={sendToSub}
              data-testid="walk-send-sub"
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2">
              <Wrench weight="fill" size={18}/> Send to Sub for Quote
            </button>
            <button onClick={downloadPDF}
              data-testid="walk-pdf"
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2">
              <FilePdf weight="fill" size={18}/> Amended Quote PDF for Leslie
            </button>
          </div>
        )}
      </div>

      {showAI && (
        <AIAssessModal projectId={project.id || project._id} projectName={project.name}
          items={items} onClose={() => setShowAI(false)} onApplied={onChanged}/>
      )}
    </div>
  );
}
